// Per-device list of news publishers the user has chosen to never see
// again. Persists in localStorage (small, synchronous — same shape as
// rankPrefs / consent). Filtering is applied in Feed.tsx alongside the
// per-session `hidden` set: hidden is one-tap-away, blocked is durable.
//
// Storage shape: a JSON array of publisher names exactly as they appear
// on ManifestEntry.publisher (the canonical-URL-derived host string,
// `www.` stripped, per the demo-pipeline jq translation step).

import { useCallback, useEffect, useState } from "preact/hooks";

/** Storage key follows the existing `agg:<surface>:v<int>` convention
 *  from `consent/state.ts` and `settings/rankPrefs.ts`. Versioned so a
 *  future schema change can bump to v2 and migrate. */
const STORAGE_KEY = "agg:blocked_publishers:v1";

/** Hard cap on the number of stored entries so a runaway loop or copy-
 *  paste paste-bomb can't blow up the localStorage budget. 256 is well
 *  above the realistic ceiling (the demo manifest only ever has ~25
 *  distinct publishers) and small enough that load/save are O(K) where
 *  K is negligible. */
export const MAX_BLOCKED = 256;

/** Read the persisted block list. Returns an empty Set when storage is
 *  unavailable / empty / malformed — never throws. */
export function loadBlockedPublishers(): Set<string> {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed
        .filter((x): x is string => typeof x === "string" && x.length > 0)
        .slice(0, MAX_BLOCKED),
    );
  } catch {
    return new Set();
  }
}

/** Persist a block set. No-op (no throw) when storage is unavailable
 *  (private mode keeps an in-memory session). */
export function saveBlockedPublishers(set: ReadonlySet<string>): void {
  if (typeof localStorage === "undefined") return;
  try {
    const arr = Array.from(set).slice(0, MAX_BLOCKED);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch {
    // Quota / disabled storage / etc. — fall through silently.
  }
}

/** React hook: blocked-publishers state with persistence on every
 *  change. The returned `block` / `unblock` setters are stable
 *  (useCallback with no deps that change) so consumers can spread them
 *  into props without forcing avoidable re-renders. */
export function useBlockedPublishers(): {
  blocked: Set<string>;
  block: (publisher: string) => void;
  unblock: (publisher: string) => void;
} {
  const [blocked, setBlocked] = useState<Set<string>>(() =>
    loadBlockedPublishers(),
  );
  useEffect(() => {
    saveBlockedPublishers(blocked);
  }, [blocked]);

  const block = useCallback((publisher: string) => {
    if (!publisher) return;
    setBlocked((prev) => {
      if (prev.has(publisher)) return prev;
      const next = new Set(prev);
      next.add(publisher);
      return next;
    });
  }, []);

  const unblock = useCallback((publisher: string) => {
    setBlocked((prev) => {
      if (!prev.has(publisher)) return prev;
      const next = new Set(prev);
      next.delete(publisher);
      return next;
    });
  }, []);

  return { blocked, block, unblock };
}
